import {
	Alert,
	Box,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	Snackbar,
	Tooltip,
	TooltipProps,
	Typography,
	tooltipClasses,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import CustomButton from '../CustomButton/CustomButton';
import CustomDataGrid from '../CustomDataGrid/CustomDataGrid';
import OrderSummary from '../OrderSummary/OrderSummary';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { setCart } from '@src/store/reducers/cartReducer';
import {
	ArrowBack,
	DecQuantityIcon,
	IncQuantityIcon,
	InfoCircleIcon,
	LockIcon,
	ModalCloseIcon,
	SearchIcon,
	SlideCancelIcon,
} from '../Icons';
import Warning from '../Warning/Warning';
import styled from 'styled-components';
import { formatPriceValue, getUniqueObjectsByKey } from '@src/helpers/Helpers';
import CustomDataGrid2 from '../CustomDataGrid/CustomDataGrid2';
import { RESTAURANT } from '@src/Constants';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import CustomPermission from '../Permissions/CustomPermission';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import SearchBar from '../SearchBar';
import { IsMobileWidth, IsTabletWidth } from '../MediaQueryUtils';
import { CustomAlertType, Filters, Option, Product, TimeoutHandler } from '@src/interfaces/type';
import ProductCustomCard from '../CustomCard/ProductCustomCard';
import SlidableDrawer from '../CustomModal/SlidableDrawer';
import ProductCard from '../CustomCard/ProductCard';
import CustomModal from '../CustomModal';
import ProductDetails from '../ProductDetails/ProductDetails';
import CustomFilter from '../CustomFilter/CustomFilter';
import ProductList from '../ProductListing/ProductList';
import { CreditLimitWarningModal } from '../CartComponent/CartComponent';

const fontStyles = {
	color: 'var(--Black, #1A1A1A)',
	fontFamily: 'Nunito Sans',
	fontSize: '18px',
	fontWeight: 700,
	lineHeight: '24px',
};

const highlightedItemStyling = {
	color: 'var(--default-text-color)',
	fontSize: '14px',
	fontWeight: 700,
};

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 408,
		borderRadius: '8px',
		border: '1px solid #DDD',
		background: '#FDFDFD',
		color: 'var(--color-font-grey)',
		fontSize: '14px',
		fontWeight: 600,
		padding: '12px',
	},
});

const StyledInfoMessage = styled.div({
	color: '#5C5C5C',
	fontSize: '13px',
	fontWeight: 500,
	padding: '16px',
	letterSpacing: '0.28px',
	flexShrink: 0, // Prevents resizing when the content overflows
});

const ProductContainer = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
});

const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
`;

const MaxPriceConatiner = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
	overflow: 'hidden',
	height: '100%',
});

const longText = `
For some items like meats and fresh vegetables, the prices may change frequently. For such items, you can set a maximum price (you will accept) in the orderbook.
`;

const productDetailsModalStyles = {
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	maxWidth: '600px',
	maxHeight: '444px',
	'@media (max-width: 600px)': {
		width: '90%',
		maxWidth: '90%',
		maxHeight: '645px',
		height: '645px',
	},
};

type Category = {
	id: number;
	value: string;
};

interface CartComponentProps {
	totalItems: number;
	total: number;
	productDetails: any;
	setProductDetails: (details: any) => void;
	setCartData: (data: any) => void;
	checkForCartData: (waId: string, id: string) => void;
	showOrderSummary: boolean;
	setShowOrderSummary: (show: boolean) => void;
	waId: string;
	restaurantId: string;
	restaurantPhone: string;
	setOpenModal: (open: boolean) => void;
	setResponseMessage: (message: CustomAlertType) => void;
	setWidth: (width: string) => void;
	setApiCall: (call: boolean) => void;
	displayCustomError: (error: string) => void;
	selectedDate: any;
	setSelectedDate: any;
	selectedDateTime: any;
	setSelectedDateTime: any;
	comment: any;
	setComment: any;
	productsBackUp: any;
	setProductsBackup: any;
	rewardsInfo: any;
	creditInfo: any;
}

function StartNewOrder({
	showOrderSummary,
	setShowOrderSummary,
	checkForCartData,
	setCartData,
	waId,
	restaurantPhone,
	setOpenModal,
	setResponseMessage,
	setWidth,
	setApiCall,
	selectedDate,
	setSelectedDate,
	selectedDateTime,
	setSelectedDateTime,
	comment,
	setComment,
	productsBackUp,
	setProductsBackup,
	rewardsInfo,
	creditInfo,
}: CartComponentProps) {
	const dispatch = useAppDispatch();

	const cart: any = useAppSelector((state) => state?.cart?.value);
	const selectedBranch: any = useAppSelector((state) => state?.restaurant?.value?.default);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	const isTabletWidth = IsTabletWidth();
	const isMobileWidth = IsMobileWidth();

	const [isLoading, setIsLoading] = useState(true);
	const [btnLoading, setBtnLoading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [orderbookItems, setOrderbookItems] = useState<Product[]>([]);
	const { apiService }: any = React.useContext(ApplicationContext);
	const restaurantId: any = useAppSelector((state) => state.restaurant.value.default?.userId);

	const [total, setTotal] = useState(0);
	const [totalItems, setTotalItems] = useState(0);
	const [warning, setWarning] = useState(false);
	const [showCreditWarningModal, setShowCreditWarningModal] = useState(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [maxPriceDrawer, setMaxPriceDrawer] = useState(false);
	const [collapse, setCollapse] = useState(false);
	const [detailsMiodal, setDetailsModal] = useState(false);
	const [productDetails, setProductDetails] = useState([]);
	const [details, setDetails] = useState([]);
	const [filters, setFilters] = useState<Filters | null>(null);
	const [activeFilter, setActiveFilter] = useState('');
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [searching, setSearching] = useState<boolean>(false);
	const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
	const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);

	const timeoutHandler = useRef<TimeoutHandler>(null);

	const checkPermission = usePermission();
	const canViewOrderBook = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.ORDER_BOOK?.VIEW, waId);
	const canCreateCart = checkPermission(ALL_PERMISSION_MAP?.RESTAURANT?.CART?.UPDATE, waId);

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			setSearching(true);
		}
	};

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const showDetails = (product: any) => {
		setDetails(product);
		setDetailsModal(true);
	};

	const columns = [
		// { field: 'id', headerName: 'Product ID', width: 120 },
		{
			field: 'name',
			headerName: 'Part Name',
			width: 300,
			renderCell: (params: any) => {
				const name = params?.row?.name;
				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
						}}
						title={name}
					>
						<img
							style={{ objectFit: 'contain' }}
							src={
								params?.row?.images?.[0]?.image ||
								params?.row?.images?.[0]?.url ||
								params?.row?.image ||
								'/placeholderImage.svg'
							}
							alt="product-img"
							width="24px"
							height="24px"
						/>
						{params?.row?.name?.length > 33 ? (
							<CustomTooltip title={params?.row?.name} placement="top">
								<p
									style={{
										maxWidth: '260px',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{params?.row?.name}
								</p>
							</CustomTooltip>
						) : (
							<p
								style={{
									maxWidth: '260px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{params?.row?.name}
							</p>
						)}
					</div>
				);
			},
		},
		// { field: 'unit', headerName: 'Unit', width: 90 },
		{
			field: 'price',
			headerName: 'Price (INR)',
			width: 90,
		},
		{ field: 'groupCategory', headerName: 'Part Group Category', width: 180 },
		{
			field: 'category',
			headerName: 'Category',
			width: 100,
		},
		{
			field: 'subCategory',
			headerName: 'Sub Category',
			width: 100,
		},
		// { field: 'sku', headerName: 'SKU', width: 100, align: 'left' },
		//new col
		{ field: 'brand', headerName: 'Brand', width: 150 },
		{ field: 'partNumber', headerName: 'Part Number', width: 150 },
		{ field: 'partDimension', headerName: 'Part Dimension ID', width: 180 },
		{ field: 'partDiameter', headerName: 'Part Diameter ID', width: 180 },
		{ field: 'partWeight', headerName: 'Part Weight', width: 150 },
		{ field: 'partQuality', headerName: 'Part Quality ID', width: 180 },
		{ field: 'partComposition', headerName: 'Part Composition ID', width: 180 },
		{ field: 'partDesign', headerName: 'Part Design ID', width: 180 },
		{ field: 'partPerformance', headerName: 'Part Performance ID', width: 180 },
		{
			field: 'maxPrice',
			headerName: (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					Max Limit
					<CustomWidthTooltip title={longText} placement="top">
						<IconButton>
							<InfoCircleIcon fill="#FFBE00" />
						</IconButton>
					</CustomWidthTooltip>
				</div>
			),
			renderCell: (params: any) =>
				params?.row?.highlight ? (
					<div style={highlightedItemStyling}>{params?.row?.maxPrice}</div>
				) : (
					params?.row?.maxPrice
				),
			width: 160,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'quantity',
			headerName: '',
			width: 130,
			renderCell: (params: any) => (
				<Box
					sx={{
						alignItems: 'center',
						width: '100%',
						height: '38px',
						display: 'flex',
						borderRadius: '4px',
						background: '#FDFDFD',
						border: '1.5px solid #F1F1F1',
						justifyContent: 'space-between',
						padding: '0 4px',
					}}
				>
					<IconButton
						size="small"
						onClick={() => decreaseQuantity(params?.row?.id)}
						disabled={false}
					>
						<DecQuantityIcon />
					</IconButton>
					<span>{params.row.quantity}</span>
					<IconButton size="small" onClick={() => increaseQuantity(params?.row?.id)}>
						<IncQuantityIcon />
					</IconButton>
				</Box>
			),
		},
		{
			field: 'recommend',
			headerName: 'Recommended',
			width: 120,
			renderCell: (params: any) => {
				return (
					<div
						id="recommend"
						onClick={() => recommendedQuantity(params?.row?.id)}
						style={{
							alignItems: 'center',
							background: '#EDEDED',
							width: '100%',
							height: '38px',
							display: 'flex',
							borderRadius: '4px',
							justifyContent: 'center',
							cursor: 'pointer',
						}}
					>
						{params.row.recommend}
					</div>
				);
			},
		},
	];

	const displayCustomError = (error: string) => {
		setErrorMessage(error);
		setErrorVisible(true);
	};

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const decreaseQuantity = (id: string) => {
		setOrderbookItems((items: any) => {
			let updatedItems: any = [];
			let updatedTotal = 0;
			let updatedQuantity = 0;
			let products: any = [];
			let backUp: any = [...productsBackUp];

			updatedItems = items.map((item: any) => {
				if (item?.id === id && Number(item?.quantity) > 0) {
					const newQuantity = Number(item?.quantity) - 1;
					updatedTotal += newQuantity * Number(item?.price);
					updatedQuantity += newQuantity;
					products.push({ ...item, unit: item?.unit, quantity: newQuantity || 0 });
					// if (newQuantity > 0) {
					// 	products.push({ ...item, unit: item?.unit, quantity: newQuantity });
					// }

					// // When item becomes zero, remove from backup
					// if (newQuantity === 0) {
					// 	backUp = backUp?.filter(
					// 		(product: any) => product?.id?.toString() !== item?.id?.toString()
					// 	);
					// }

					return { ...item, quantity: newQuantity };
				}
				updatedTotal += Number(item?.quantity) * Number(item?.price);
				updatedQuantity += Number(item?.quantity);
				if (item?.quantity > 0) {
					products.push({ ...item, unit: item?.unit, quantity: item?.quantity });
				}
				return item;
			});

			// Merge updatedItems with productsBackup
			const backUpProducts = [...backUp, ...products];
			const uniqueProducts = Array.from(
				new Map(backUpProducts.map((item) => [item.productId, item])).values()
			);

			// Calculate the total and totalItems considering productsBackup
			const finalTotalItems = uniqueProducts.reduce((acc, curr) => acc + curr.quantity, 0);
			const finalTotal = formatPriceValue(
				uniqueProducts.reduce(
					(acc, curr) => acc + parseFloat(curr.price) * Number(curr.quantity),
					0
				)
			);

			setProductsBackup(uniqueProducts);
			setTotalItems(finalTotalItems);
			setTotal(finalTotal);

			return updatedItems;
		});
	};

	const increaseQuantity = (id: string) => {
		setOrderbookItems((items: any) => {
			let updatedItems: any = [];
			let updatedTotal = 0;
			let updatedQuantity = 0;
			let products: any = [];

			updatedItems = items.map((item: any) => {
				if (item?.id === id) {
					const newQuantity = Number(item?.quantity) + 1;
					updatedTotal += newQuantity * Number(item?.price);
					updatedQuantity += newQuantity;
					if (newQuantity > 0) {
						products.push({ ...item, unit: item?.unit, quantity: newQuantity });
					}
					return { ...item, quantity: newQuantity };
				}
				updatedTotal += Number(item?.quantity) * Number(item?.price);
				updatedQuantity += Number(item?.quantity);
				if (item?.quantity > 0) {
					products.push({ ...item, unit: item?.unit, quantity: item?.quantity });
				}
				return item;
			});

			// Merge updatedItems with productsBackup
			const backUpProducts = [...productsBackUp, ...products];
			const uniqueProducts = Array.from(
				new Map(backUpProducts.map((item) => [item.productId, item])).values()
			);

			// Calculate the total and totalItems considering productsBackup
			const finalTotalItems = uniqueProducts.reduce((acc, curr) => acc + curr.quantity, 0);
			const finalTotal = formatPriceValue(
				uniqueProducts.reduce(
					(acc, curr) => acc + parseFloat(curr.price) * Number(curr.quantity),
					0
				)
			);

			setProductsBackup(uniqueProducts);
			setTotalItems(finalTotalItems);
			setTotal(finalTotal);

			return updatedItems;
		});
	};

	const recommendedQuantity = (id: string) => {
		setOrderbookItems((items: any) => {
			let updatedItems: any = [];
			let updatedTotal = 0;
			let updatedQuantity = 0;
			let products: any = [];

			updatedItems = items?.map((item: any) => {
				if (item?.id === id) {
					const recommendedQty = item?.recommend || 1;
					updatedTotal += recommendedQty * Number(item?.price);
					updatedQuantity += recommendedQty;
					if (recommendedQty > 0) {
						products.push({ ...item, unit: item?.unit, quantity: recommendedQty });
					}
					return { ...item, quantity: recommendedQty };
				}
				updatedTotal += Number(item?.quantity) * Number(item?.price);
				updatedQuantity += Number(item?.quantity);
				if (item?.quantity > 0) {
					products.push({ ...item, unit: item?.unit, quantity: item?.quantity });
				}
				return item;
			});

			// Merge updatedItems with productsBackup
			const backUpProducts = [...productsBackUp, ...products];
			const uniqueProducts = Array.from(
				new Map(backUpProducts.map((item) => [item.productId, item])).values()
			);

			// Calculate the total and totalItems considering productsBackup
			const finalTotalItems = uniqueProducts.reduce((acc, curr) => acc + curr.quantity, 0);
			const finalTotal = formatPriceValue(
				uniqueProducts.reduce(
					(acc, curr) => acc + parseFloat(curr.price) * Number(curr.quantity),
					0
				)
			);

			setProductsBackup(uniqueProducts.filter((item) => item?.quantity > 0));
			setTotalItems(finalTotalItems);
			setTotal(finalTotal);

			return updatedItems;
		});
	};

	const handleCounterChange = (id: string, newQuantity: number) => {
		setOrderbookItems((items: any) => {
			let updatedItems: any = [];
			let updatedTotal = 0;
			let updatedQuantity = 0;
			let products: any = [];

			updatedItems = items.map((item: any) => {
				if (item?.id === id) {
					updatedTotal += newQuantity * Number(item?.price);
					updatedQuantity += newQuantity;
					products.push({ ...item, unit: item?.unit, quantity: newQuantity || 0 });

					return { ...item, quantity: newQuantity };
				}
				updatedTotal += Number(item?.quantity) * Number(item?.price);
				updatedQuantity += Number(item?.quantity);
				if (item?.quantity > 0) {
					products.push({ ...item, unit: item?.unit, quantity: item?.quantity });
				}
				return item;
			});

			// Merge updatedItems with productsBackup
			const backUpProducts = [...productsBackUp, ...products];
			console.log(backUpProducts);

			const uniqueProducts = Array.from(
				new Map(backUpProducts.map((item) => [item.productId, item])).values()
			);

			// Calculate the total and totalItems considering productsBackup
			const finalTotalItems = uniqueProducts.reduce((acc, curr) => acc + curr.quantity, 0);
			const finalTotal = formatPriceValue(
				uniqueProducts.reduce(
					(acc, curr) => acc + parseFloat(curr.price) * Number(curr.quantity),
					0
				)
			);

			setProductsBackup(uniqueProducts);
			setTotalItems(finalTotalItems);
			setTotal(finalTotal);

			return updatedItems;
		});
	};

	const getSelectedItemQuantity = (id: string, quantity: number) => {
		const product = productsBackUp?.find(
			(product: any) =>
				product?.id?.toString() === id?.toString() ||
				product?.productId?.toString() === id?.toString()
		);
		return product?.quantity || quantity;
	};

	const loadMore = () => {
		setLoadingProducts(true);
		//@ts-ignore
		setPage((prevState: number) => prevState + 1);
	};

	const getOrderBookItems = (waId: any, id: string) => {
		const limit = 25;
		let products: any = [];

		apiService.getOrderbookItems(
			(response: AxiosResponse) => {
				const data = response?.data?.data;
				const modifiedRowsData = data?.items?.map((item: any) => {
					const selectedQuantity = getSelectedItemQuantity(
						item?.productId?.toString(),
						item?.quantity
					);
					//When editing cart
					if (selectedQuantity > 0) {
						//when switching between pages to maintain selected product state
						products?.push({
							...item,
							id: item?.id || item?.productId,
							quantity: Number(selectedQuantity),
						});
					}

					return {
						...item,
						name: item?.productName,
						category: item?.category?.name,
						categoryId: item?.category?.id,
						categoryObject: { id: item?.category?.id, value: item?.category?.name },
						id: item?.productId?.toString(),
						unit: item?.unit,
						price: formatPriceValue(item?.customerPrice),
						quantity: selectedQuantity,
						recommend: item?.recommend,
						subCategory: item?.subCategory?.name,
						subCategoryId: item?.subCategory?.id,
						maxPrice: formatPriceValue(item?.maxPrice),
						machineBrand: item?.machineBrand?.name,
						machineBrandId: item?.machineBrand?.id,
						machineModel: item?.machineModel?.name,
						machineModelId: item?.machineModel?.id,
						groupCategory: item?.groupCategory?.name,
						groupCategoryId: item?.groupCategory?.id,
						brand: item?.brand?.name,
						brandId: item?.brand?.id,
						partComposition: item?.partComposition?.name,
						partCompositionId: item?.partComposition?.id,
						partDesign: item?.partDesign?.name,
						partDesignId: item?.partDesign?.id,
						partDiameter: item?.partDiameter?.name,
						partDiameterId: item?.partDiameter?.id,
						partDimension: item?.partDimension?.name,
						partDimensionId: item?.partDimension?.id,
						partOwnership: item?.partOwnership?.name,
						partOwnershipId: item?.partOwnership?.id,
						partQuality: item?.partQuality?.name,
						partQualityId: item?.partQuality?.id,
						partWeight: item?.partWeight?.name,
						partWeightId: item?.partWeight?.id,
						partPerformance: item?.partPerformance?.name,
						partPerformanceId: item?.partPerformance?.id,
					};
				});

				const totalCount: number = data?.totalCount;
				const totalPages: number = Math.max(Math.ceil(totalCount / limit), 1);
				const backUpProducts = [...productsBackUp, ...products];
				const uniqueProducts = Array.from(
					new Map(backUpProducts.map((item) => [item?.productId, item])).values()
				);

				setTotalPages(totalPages);
				setTotalCount(totalCount);
				setProductsBackup(uniqueProducts);
				setItemsAndTotal(uniqueProducts);

				if (page === 1) {
					setOrderbookItems(modifiedRowsData);
				} else {
					//@ts-ignore
					setOrderbookItems((prevState: any) => [...prevState, ...modifiedRowsData]);
				}

				setTotalPages(totalPages);
				setTotalCount(totalCount);

				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			(error: AxiosError) => {
				console.log(error);

				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			waId,
			id,
			{
				page: page,
				limit: limit,
				search: searchValue,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	const getProducts = (restaurantPhone: string) => {
		const limit = 25;
		let products: any = [];

		apiService.getAllRestaurantProducts(
			(response: AxiosResponse) => {
				const data = response?.data?.data;
				const modifiedRowsData = data?.products?.map((item: any) => {
					const selectedQuantity = getSelectedItemQuantity(
						item?.productId?.toString(),
						item?.quantity
					);
					//When editing cart
					if (selectedQuantity > 0) {
						//when switching between pages to maintain selected product state
						products?.push({
							...item,
							id: item?.id || item?.productId,
							quantity: Number(selectedQuantity),
						});
					}
					return {
						...item,
						name: item?.productName,
						category: item?.category?.name,
						categoryId: item?.category?.id,
						categoryObject: { id: item?.category?.id, value: item?.category?.name },
						id: item?.productId?.toString(),
						unit: item?.unit,
						price: formatPriceValue(item?.customerPrice),
						quantity: selectedQuantity,
						recommend: item?.recommended,
						maxPrice: formatPriceValue(item?.maxPrice),
						subCategory: item?.subCategory?.name,
						subCategoryId: item?.subCategory?.id,
						machineBrand: item?.machineBrand?.name,
						machineBrandId: item?.machineBrand?.id,
						machineModel: item?.machineModel?.name,
						machineModelId: item?.machineModel?.id,
						groupCategory: item?.groupCategory?.name,
						groupCategoryId: item?.groupCategory?.id,
						brand: item?.brand?.name,
						brandId: item?.brand?.id,
						partComposition: item?.partComposition?.name,
						partCompositionId: item?.partComposition?.id,
						partDesign: item?.partDesign?.name,
						partDesignId: item?.partDesign?.id,
						partDiameter: item?.partDiameter?.name,
						partDiameterId: item?.partDiameter?.id,
						partDimension: item?.partDimension?.name,
						partDimensionId: item?.partDimension?.id,
						partOwnership: item?.partOwnership?.name,
						partOwnershipId: item?.partOwnership?.id,
						partQuality: item?.partQuality?.name,
						partQualityId: item?.partQuality?.id,
						partWeight: item?.partWeight?.name,
						partWeightId: item?.partWeight?.id,
						partPerformance: item?.partPerformance?.name,
						partPerformanceId: item?.partPerformance?.id,
					};
				});
				const totalCount: number = data?.totalCount;
				const totalPages: number = Math.max(Math.ceil(totalCount / limit), 1);
				const backUpProducts = [...productsBackUp, ...products];
				const uniqueProducts = Array.from(
					new Map(backUpProducts.map((item) => [item?.productId, item])).values()
				);

				setTotalPages(totalPages);
				setTotalCount(totalCount);
				setProductsBackup(uniqueProducts);
				setItemsAndTotal(uniqueProducts);

				if (page === 1) {
					setOrderbookItems(modifiedRowsData);
				} else {
					//@ts-ignore
					setOrderbookItems((prevState: any) => [...prevState, ...modifiedRowsData]);
				}

				setTotalPages(totalPages);
				setTotalCount(totalCount);

				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			(error: AxiosError) => {
				console.log(error);
				setIsLoading(false);
				setLoadingProducts(false);
				setSearching(false);
			},
			restaurantPhone,
			{
				page: page,
				limit: limit,
				search: searchValue,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	const productsTable = () => {
		const commonTable = () => {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<SearchBar
						style={{
							width: isTabletWidth ? '100%' : '312px',
							height: '36px',
						}}
						value={searchValue}
						onChange={handleSearch}
						isLoading={false}
					/>
					<CustomFilter
						filters={filters}
						setFilters={setFilters}
						activeFilter={activeFilter}
						setActiveFilter={setActiveFilter}
						products={orderbookItems}
						setFilteredProducts={setOrderbookItems}
						setLoading={setSearching}
						filterType="server"
						setPage={setPage}
					/>
					<Divider />
					{orderbookItems?.length ? (
						<ProductList
							//@ts-ignore
							products={orderbookItems}
							searching={searching}
							loadingProducts={loadingProducts}
							loadMore={loadMore}
							showDetails={showDetails}
							style={{
								overflow: 'auto',
								height: isMobileWidth ? 'calc(100vh - 200px)' : 'calc(100vh - 400px)',
							}}
							totalCount={totalCount}
							noCheckbox
							view="order"
							increaseQuantity={increaseQuantity}
							decreaseQuantity={decreaseQuantity}
							recommendedQuantity={recommendedQuantity}
							onCounterChange={handleCounterChange}
						/>
					) : (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 'calc(100vh - 400px)',
							}}
						>
							No Products Found
						</div>
					)}
				</Box>
			);
		};
		if (isRestaurant) {
			if (canViewOrderBook) {
				return commonTable();
			} else {
				return (
					<CustomPermission
						icon={<LockIcon />}
						title="We are sorry..."
						paragraph="You do not have permission to view orderbook items. Contact 
	an administrator to get permission."
					/>
				);
			}
		} else {
			return commonTable();
		}
	};

	// const productListing = () => {
	// 	const commonContainer = () => {
	// 		if (orderbookItems?.length) {
	// 			return (
	// 				<ProductContainer>
	// 					{orderbookItems?.map((item) => (
	// 						<ProductCustomCard
	// 							key={item?.id}
	// 							product={item}
	// 							increaseQuantity={increaseQuantity}
	// 							decreaseQuantity={decreaseQuantity}
	// 							recommendedQuantity={recommendedQuantity}
	// 						/>
	// 					))}
	// 				</ProductContainer>
	// 			);
	// 		}
	// 		return (
	// 			<div
	// 				style={{
	// 					display: 'flex',
	// 					justifyContent: 'center',
	// 					alignItems: 'center',
	// 					width: '100%',
	// 					color: '#7D7D7D',
	// 					flexGrow: 1,
	// 					fontSize: '12px',
	// 					padding: '16px',
	// 				}}
	// 			>
	// 				{isRestaurant
	// 					? 'Create the order book in the orderbook section before proceeding'
	// 					: 'No Products Found'}
	// 			</div>
	// 		);
	// 	};
	// 	if (isRestaurant) {
	// 		if (canViewOrderBook) {
	// 			return commonContainer();
	// 		} else {
	// 			return (
	// 				<CustomPermission
	// 					icon={<LockIcon />}
	// 					title="We are sorry..."
	// 					paragraph="You do not have permission to view orderbook items. Contact
	// an administrator to get permission."
	// 				/>
	// 			);
	// 		}
	// 	} else {
	// 		return commonContainer();
	// 	}
	// };

	const addItemsToCart = (data: any, waId: any, id: string) => {
		const items = data?.length
			? data
			: productsBackUp?.map((product: any) => {
					return { id: product?.id || product?.productId, quantity: Number(product?.quantity) };
			  });

		setBtnLoading(true);
		setIsDisabled(true);

		apiService.addCartItems(
			{ products: items },
			(response: AxiosResponse) => {
				setBtnLoading(false);
				setIsDisabled(false);
				setShowOrderSummary(true);
				// setWidth('1118px');
				setApiCall(true);
				setWarning(false);
				const updatedOrderbook: any = orderbookItems?.map((item) => {
					return { ...item, highlight: false };
				});
				setOrderbookItems(updatedOrderbook);
			},
			(error: AxiosError) => {
				console.log(error);
				setBtnLoading(false);
				setIsDisabled(false);
				displayCustomError('Something went wrong!');
			},
			selectedBranch?.phone,
			waId
		);
	};

	const maxPriceWarning = (data: any, waId: any, id: string) => {
		if (
			creditInfo?.allowNewOrders == false &&
			creditInfo?.isActive &&
			creditInfo?.creditAvailable <= 0
		) {
			setShowCreditWarningModal(true);
			return;
		}
		const ids = data?.products?.map((item: any) => item?.id?.toString());
		const selectedProducts = orderbookItems?.filter((item) => ids.includes(item?.id?.toString()));

		const highlightedProductsIds = selectedProducts
			?.filter((item) => {
				const maxPrice = Number(item?.maxPrice);
				const price = Number(item?.price);
				return maxPrice && maxPrice !== 0 ? price > maxPrice : false;
			})
			?.map((item) => item?.id?.toString());

		if (highlightedProductsIds?.length) {
			const updatedOrderbook = orderbookItems?.map((item) => {
				if (highlightedProductsIds?.includes(item?.id?.toString())) {
					return { ...item, highlight: true };
				}
				return item;
			});
			setOrderbookItems(updatedOrderbook);

			setWarning(true);
			setIsDisabled(true);
			if (isMobileWidth) {
				setMaxPriceDrawer(true);
			}
		} else {
			addItemsToCart(data, waId, id);
		}
	};

	const handleSearch = (value: any) => {
		setSearching(true);
		setSearchValue(value);
		setPage(1);
	};

	useEffect(() => {
		if (isRestaurant) {
			if (canViewOrderBook) {
				getOrderBookItems(waId, restaurantId);
			} else {
				setIsLoading(false);
			}
		} else {
			getProducts(restaurantPhone);
		}
	}, [page, filters]);

	useEffect(() => {
		//debounce
		if (timeoutHandler.current) {
			clearTimeout(timeoutHandler.current);
		}
		timeoutHandler.current = setTimeout(() => {
			try {
				if (isRestaurant) {
					getOrderBookItems(waId, restaurantId);
				} else {
					getProducts(restaurantPhone);
				}
			} catch (err) {}
		}, 500);
	}, [searchValue]);

	const setItemsAndTotal = (productsBackUp: any) => {
		setTotalItems(productsBackUp?.reduce((acc: any, curr: any) => acc + curr?.quantity, 0) || 0);
		setTotal(
			formatPriceValue(
				productsBackUp?.reduce(
					(acc: any, curr: any) =>
						acc + parseFloat(curr?.customerPrice || curr?.price) * Number(curr?.quantity),
					0
				)
			) || 0
		);
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	// const mobileView = () => {
	// 	return (
	// 		<Box
	// 			sx={{
	// 				width: '100%',
	// 				height: '100vh', // Ensure the Box takes the full viewport height
	// 				display: 'flex',
	// 				flexDirection: 'column', // Arrange children in a column
	// 				overflow: 'hidden', // Prevents the parent Box from scrolling
	// 			}}
	// 		>
	// 			<Box
	// 				display="flex"
	// 				justifyContent="space-between"
	// 				alignItems="center"
	// 				px={2}
	// 				width="100%"
	// 				height="56px"
	// 				flexShrink={0} // Prevents resizing when the content overflows
	// 			>
	// 				<div style={{ display: 'flex', alignItems: 'center' }}>
	// 					<IconButton
	// 						onClick={() => {
	// 							setOpenModal(false);
	// 						}}
	// 						size="large"
	// 					>
	// 						<ArrowBack />
	// 					</IconButton>
	// 					<Box>
	// 						<p
	// 							style={{
	// 								color: '#5C5C5C',
	// 								fontFamily: 'Poppins',
	// 								fontSize: '16px',
	// 								fontWeight: 400,
	// 							}}
	// 						>
	// 							Start Order
	// 						</p>
	// 					</Box>
	// 				</div>
	// 				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
	// 					<div style={{ color: '#333333', fontSize: '13px', fontWeight: 500 }}>
	// 						Qty: {totalItems} items
	// 					</div>
	// 					<IconButton onClick={() => setCollapse(!collapse)}>
	// 						<SearchIcon />
	// 					</IconButton>
	// 				</div>
	// 			</Box>
	// 			<Divider style={{ width: '100%' }} />

	// 			{collapse ? (
	// 				<Collapse
	// 					in={collapse}
	// 					style={{
	// 						display: 'flex',
	// 						gap: '15px',
	// 						alignItems: 'center',
	// 						flexShrink: 0,
	// 						padding: '16px',
	// 					}}
	// 				>
	// 					<SearchBar
	// 						style={{
	// 							width: '328px',
	// 							height: '36px',
	// 						}}
	// 						value={searchValue}
	// 						onChange={handleSearch}
	// 						isLoading={false}
	// 						autoFocus
	// 					/>
	// 				</Collapse>
	// 			) : null}
	// 			<StyledInfoMessage>
	// 				<span style={{ color: '#1A1D1F' }}>Max Limit </span>
	// 				for some items like meats and fresh vegetables, the prices may change frequently. For such
	// 				items, you can set a maximum price (you will accept) in the orderbook.
	// 			</StyledInfoMessage>
	// 			{isLoading ? (
	// 				<Box
	// 					sx={{
	// 						width: '100%',
	// 						flexGrow: 1, // Ensure it takes up remaining space
	// 						display: 'flex',
	// 						alignItems: 'center',
	// 						justifyContent: 'center',
	// 					}}
	// 				>
	// 					<div className="chat__body__loadingMore__wrapper">
	// 						<CircularProgress size={28} />
	// 					</div>
	// 				</Box>
	// 			) : (
	// 				productListing()
	// 			)}
	// 			<StyledBottomBtnContainer>
	// 				<div
	// 					style={{
	// 						display: 'flex',
	// 						flexDirection: 'column',
	// 						color: '#5C5C5C',
	// 						fontSize: '11px',
	// 						fontWeight: 600,
	// 					}}
	// 				>
	// 					<span>Total Amount:</span>
	// 					<span style={{ color: '#1A1D1F', fontSize: '14px' }}>₹ {total}</span>
	// 				</div>
	// 				{canCreateCart ? (
	// 					<CustomButton
	// 						title={
	// 							btnLoading ? (
	// 								<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
	// 							) : (
	// 								'Checkout'
	// 							)
	// 						}
	// 						styles={{ width: '117px', height: '40px' }}
	// 						heightVariant="medium"
	// 						color="primary"
	// 						disabled={isDisabled || totalItems === 0}
	// 						handleClick={() => {
	// 							if (totalItems > 0) {
	// 								maxPriceWarning({ products: cartItems }, waId, restaurantId);
	// 							}
	// 						}}
	// 					/>
	// 				) : (
	// 					<CustomTooltip
	// 						title="You do not have permission to create cart. Contact
	// 						an administrator to get permission."
	// 						placement="bottom"
	// 					>
	// 						<span>
	// 							<CustomButton
	// 								title="Checkout"
	// 								styles={{ width: '117px', height: '40px' }}
	// 								heightVariant="medium"
	// 								color="primary"
	// 								disabled
	// 								handleClick={() => {}}
	// 							/>
	// 						</span>
	// 					</CustomTooltip>
	// 				)}
	// 			</StyledBottomBtnContainer>

	// 			{maxPriceDrawer ? (
	// 				<SlidableDrawer
	// 					isOpen={maxPriceDrawer}
	// 					setIsOpen={setMaxPriceDrawer}
	// 					onClose={() => {
	// 						setIsDisabled(false);
	// 					}}
	// 				>
	// 					<MaxPriceConatiner>
	// 						<div style={{ padding: '16px', marginTop: '10px', flexShrink: 0 }}>
	// 							{warning ? (
	// 								<Warning
	// 									onClick={() => {
	// 										if (Object.keys(cartItems).length !== 0) {
	// 											addItemsToCart({ products: cartItems }, waId, restaurantId);
	// 										}
	// 										setMaxPriceDrawer(false);
	// 									}}
	// 									message="The prices of some items have been updated by the supplier. Please review the new prices before checkout."
	// 								/>
	// 							) : null}
	// 						</div>
	// 						<div
	// 							style={{
	// 								display: 'flex',
	// 								flexDirection: 'column',
	// 								gap: '4px',
	// 								overflow: 'auto',
	// 								flexGrow: 1,
	// 							}}
	// 						>
	// 							{orderbookItems
	// 								?.filter((item) => item?.highlight)
	// 								?.map((item) => {
	// 									return (
	// 										<ProductCustomCard
	// 											style={{ boxShadow: 'none', background: '#FFFDF5' }}
	// 											key={item?.id}
	// 											product={item}
	// 											increaseQuantity={increaseQuantity}
	// 											decreaseQuantity={decreaseQuantity}
	// 											recommendedQuantity={recommendedQuantity}
	// 											showQuantity
	// 										/>
	// 									);
	// 								})}
	// 						</div>
	// 					</MaxPriceConatiner>
	// 				</SlidableDrawer>
	// 			) : null}
	// 		</Box>
	// 	);
	// };

	const modals = () => {
		if (detailsMiodal && details) {
			return (
				<CustomModal
					open={detailsMiodal}
					setOpen={setDetailsModal}
					header={false}
					style={productDetailsModalStyles}
				>
					{/* @ts-ignore */}
					<ProductDetails data={details} setOpenModal={setDetailsModal} />
				</CustomModal>
			);
		}
	};
	return showOrderSummary ? (
		<>
			<OrderSummary
				totalItems={totalItems}
				setShowOrderSummary={setShowOrderSummary}
				total={total}
				productDetails={productsBackUp}
				setProductDetails={setProductsBackup}
				waId={waId}
				setCartData={setCartData}
				restaurantId={restaurantId}
				checkForCartData={checkForCartData}
				setOpenModal={setOpenModal}
				setResponseMessage={setResponseMessage}
				setWidth={setWidth}
				setApiCall={setApiCall}
				displayCustomError={displayCustomError}
				selectedDate={selectedDate}
				setSelectedDate={setSelectedDate}
				selectedDateTime={selectedDateTime}
				setSelectedDateTime={setSelectedDateTime}
				comment={comment}
				setComment={setComment}
				showDetails={showDetails}
				rewardsInfo={rewardsInfo}
				creditInfo={creditInfo}
			/>
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
				sx={{
					'.MuiPaper-root': {
						width: 'auto !important',
						backgroundColor: 'rgb(253, 237, 237) !important',
					},
				}}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
			{modals()}
		</>
	) : (
		<Box
			sx={{
				width: '100%',
				'@media (max-width: 978px)': {
					width: '100%',
				},
			}}
		>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={2}
				width="100%"
				height="56px"
			>
				<Box>
					<p style={fontStyles}>Start New Order</p>
				</Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						borderTop: '1.5px solid #F1F1F1',
						p: '16px',
					}}
				>
					<Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
						{canCreateCart ? (
							<CustomButton
								title={
									btnLoading ? (
										<CircularProgress size={16} style={{ color: 'var(--default-text-color)' }} />
									) : (
										'Check Out'
									)
								}
								styles={{ width: '110px' }}
								heightVariant="medium"
								color="primary"
								disabled={isDisabled || totalItems === 0}
								handleClick={() => {
									if (totalItems > 0) {
										const items = productsBackUp?.map((item: any) => {
											return { id: item?.id || item?.producId, quantity: item?.quantity };
										});
										maxPriceWarning({ products: items }, waId, restaurantId);
									}
								}}
							/>
						) : (
							<CustomTooltip
								title="You do not have permission to create cart. Contact 
							an administrator to get permission."
								placement="bottom"
							>
								<span>
									<CustomButton
										title="Check Out"
										styles={{ width: '110px' }}
										heightVariant="medium"
										color="primary"
										disabled
										handleClick={() => {}}
									/>
								</span>
							</CustomTooltip>
						)}
						<Box
							sx={{ cursor: 'pointer' }}
							onClick={() => {
								setOpenModal(false);
								setProductsBackup([]);
								setShowOrderSummary(false);
							}}
						>
							<ModalCloseIcon />
						</Box>
					</Box>
				</Box>
			</Box>
			<Divider style={{ width: '100%' }} />
			<>
				{isLoading ? (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<div className="chat__body__loadingMore__wrapper">
							<CircularProgress size={28} />
						</div>
					</Box>
				) : (
					<Box sx={{ m: '20px' }}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '20px',
								// height: '378px',
								// '@media (max-width: 978px)': {
								// 	height: '430px',
								// },
							}}
						>
							{/* <Box>
						<p style={fontStyles}>Start New Order</p>
					</Box> */}
							{warning ? (
								<Warning
									onClick={() => {
										if (productsBackUp?.length !== 0) {
											addItemsToCart(waId, restaurantId);
										}
									}}
									message="The prices of some items have been updated by the supplier. Please review the new prices before checkout."
								/>
							) : null}
							{productsTable()}
							<StyledBottomBtnContainer>
								<Box
									display="flex"
									justifyContent="flex-end"
									width="100%"
									sx={{
										'@media (max-width: 600px)': {
											justifyContent: 'space-between',
										},
									}}
								>
									<Typography variant="body2" sx={{ mr: '46px' }}>
										Qty: {totalItems} items
									</Typography>
									<div>
										<Typography variant="body1">Total Amount</Typography>
										<Typography variant="body1">₹ {total}</Typography>
									</div>
								</Box>
							</StyledBottomBtnContainer>
						</Box>
					</Box>
				)}
			</>
			{modals()}
			<CreditLimitWarningModal
				showCreditWarningModal={showCreditWarningModal}
				setShowCreditWarningModal={setShowCreditWarningModal}
				setOpenModal={setOpenModal}
				creditInfo={creditInfo}
			/>
		</Box>
	);
}

export default StartNewOrder;
